/**
 * QuickshopDialog — closes the quick shop modal itself on a successful add to cart, so it
 * doesn't stack in the top layer with the cart drawer that opens next (mirrors `QuickAddDialog`
 * in `src/js/horizon/quick-add.js`; `cart-drawer.js` waits for this to finish closing the same
 * way it already waits for quick add).
 */
import { DialogComponent, DialogCloseEvent } from "@theme/dialog";
import { ThemeEvents } from "@theme/events";

class QuickshopDialog extends DialogComponent {
  #abortController = new AbortController();

  /** @type {{ parent: Node, before: Node | null } | null} */
  #originalPosition = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#restorePosition, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * When opened from a search result, this element sits inside the search modal's own `<dialog>`
   * (rendered inline per product card). A closed `<dialog>` is `display: none` by default, and
   * that hides its whole subtree — so once the search modal closes, `showModal()` on anything
   * still nested inside it throws instead of opening. Move this dialog out to `document.body`
   * first so closing the search modal can't block it, then close the search modal.
   */
  async showDialog() {
    const searchModal = this.closest("#search-modal");
    if (searchModal instanceof DialogComponent) {
      this.#originalPosition = { parent: /** @type {Node} */ (this.parentNode), before: this.nextSibling };
      document.body.append(this);
      await searchModal.closeDialog();
    }
    super.showDialog();
  }

  /** Moves this dialog back to its spot in the search results once it's closed. */
  #restorePosition = () => {
    if (!this.#originalPosition) return;
    const { parent, before } = this.#originalPosition;
    parent.insertBefore(this, before);
    this.#originalPosition = null;
  };

  /** @param {CustomEvent<{data: {didError?: boolean}}>} event */
  #handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };
}

if (!customElements.get("quickshop-dialog")) {
  customElements.define("quickshop-dialog", QuickshopDialog);
}
